import { isPlainObject } from "lodash-es";

export interface ViolationEntryFormOverride<TField = any> {
  update?: Record<string, Partial<TField>>;
  order?: string[];
}

const mergeDeep = <T>(base: T, patch: any): T => {
  if (patch === undefined || patch === null) {
    return base;
  }
  if (!isPlainObject(base) || !isPlainObject(patch)) {
    return structuredClone(patch) as T;
  }
  const baseObj = base as Record<string, any>;
  const patchObj = patch as Record<string, any>;
  const out = Object.entries(patchObj).reduce<Record<string, any>>(
    (acc, [key, patchValue]) => {
      if (patchValue === undefined) {
        return acc;
      }

      const baseValue = baseObj[key];
      if (isPlainObject(baseValue) && isPlainObject(patchValue)) {
        acc[key] = mergeDeep(baseValue, patchValue);
      } else {
        acc[key] = structuredClone(patchValue);
      }

      return acc;
    },
    { ...baseObj },
  );

  return out as T;
};

const getFieldId = (field: any): string | undefined => {
  return field?.properties?.id;
};

export const applyViolationEntryOverride = <TField = any>(
  baseForm: TField[],
  override?: ViolationEntryFormOverride<TField>,
): TField[] => {
  const base = structuredClone(Array.isArray(baseForm) ? baseForm : []);
  if (!override || typeof override !== "object") {
    return base;
  }
  let merged = base;
  if (override.update && typeof override.update === "object") {
    const updateMap = override.update as Record<string, any>;
    merged = merged.map((field: any) => {
      const id = getFieldId(field);
      if (!id || !updateMap[id]) {
        return field;
      }
      return mergeDeep(field, structuredClone(updateMap[id]));
    });
  }
  if (Array.isArray(override.order) && override.order.length) {
    const orderIndex = new Map<string, number>();
    override.order.forEach((id, idx) => {
      orderIndex.set(id, idx);
    });

    merged = merged
      .map((field: any, idx: number) => ({ field, idx }))
      .sort((a: any, b: any) => {
        const aId = getFieldId(a.field);
        const bId = getFieldId(b.field);
        const aHas = !!aId && orderIndex.has(aId);
        const bHas = !!bId && orderIndex.has(bId);
        if (aHas && bHas) {
          return (
            (orderIndex.get(aId) as number) - (orderIndex.get(bId) as number)
          );
        }
        if (aHas) {
          return -1;
        }
        if (bHas) {
          return 1;
        }
        return a.idx - b.idx;
      })
      .map((x: any) => x.field);
  }
  return merged;
};
